import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpotifyLibraryModal = ({ 
  isOpen, 
  onClose, 
  onBack,
  onSelectTrack,
  sessionId,
  userToken,
  activePlaylistForAdding,
  addTrackToPlaylist,
  setActiveTab: setMainActiveTab,
  setShowPlaylistDetailModal,
  setCurrentPlaylist
}) => {
  const [activeTab, setActiveTab] = useState('liked'); // 'liked' or 'playlists'
  const [likedSongs, setLikedSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistTracks, setPlaylistTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');

  // Function to refresh Spotify token
  const refreshSpotifyToken = async () => {
    const refreshToken = localStorage.getItem('spotify_refresh_token');
    if (!refreshToken) {
      console.error('No refresh token available');
      return null;
    }
    
    try {
      const response = await axios.post(`${API}/spotify/auth/refresh`, null, {
        params: { refresh_token: refreshToken }
      });
      
      if (response.data.access_token) {
        localStorage.setItem('spotify_access_token', response.data.access_token);
        setSpotifyToken(response.data.access_token);
        return response.data.access_token;
      }
    } catch (err) {
      console.error('Failed to refresh Spotify token:', err);
      // Clear tokens if refresh fails
      localStorage.removeItem('spotify_access_token');
      localStorage.removeItem('spotify_refresh_token');
      setError('Session expired. Please reconnect Spotify.');
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      // Get Spotify token from localStorage
      const token = localStorage.getItem('spotify_access_token');
      if (!token) {
        setError('Please connect Spotify in Social Hub first');
        return;
      }
      setSpotifyToken(token);
      fetchLikedSongs(token, 0);
      fetchPlaylists(token);
    }
  }, [isOpen]);

  const fetchLikedSongs = async (token, newOffset = 0, isRetry = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/spotify/liked-songs`, {
        params: {
          access_token: token,
          limit: 10,
          offset: newOffset
        }
      });
      setLikedSongs(response.data.tracks);
      setOffset(newOffset);
    } catch (err) {
      console.error('Failed to fetch liked songs:', err);
      // Check if it's a 401 (token expired) and try to refresh
      if (err.response?.status === 401 && !isRetry) {
        console.log('Token expired, attempting refresh...');
        const newToken = await refreshSpotifyToken();
        if (newToken) {
          return fetchLikedSongs(newToken, newOffset, true);
        }
      }
      setError('Failed to load liked songs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylists = async (token, newOffset = 0, isRetry = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/spotify/user/playlists`, {
        params: { access_token: token }
      });
      const allPlaylists = response.data.playlists || [];
      
      // Get random 10 playlists
      if (allPlaylists.length > 10) {
        const shuffled = [...allPlaylists].sort(() => 0.5 - Math.random());
        setPlaylists(shuffled.slice(0, 10));
      } else {
        setPlaylists(allPlaylists);
      }
    } catch (err) {
      console.error('Failed to fetch playlists:', err);
      // Check if it's a 401 (token expired) and try to refresh
      if (err.response?.status === 401 && !isRetry) {
        console.log('Token expired, attempting refresh...');
        const newToken = await refreshSpotifyToken();
        if (newToken) {
          return fetchPlaylists(newToken, newOffset, true);
        }
      }
      setError('Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylistTracks = async (playlistId, newOffset = 0, token = spotifyToken, isRetry = false) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/spotify/playlist/${playlistId}/tracks`, {
        params: {
          access_token: token,
          limit: 10,
          offset: newOffset
        }
      });
      setPlaylistTracks(response.data.tracks);
      setOffset(newOffset);
    } catch (err) {
      console.error('Failed to fetch playlist tracks:', err);
      // Check if it's a 401 (token expired) and try to refresh
      if (err.response?.status === 401 && !isRetry) {
        console.log('Token expired, attempting refresh...');
        const newToken = await refreshSpotifyToken();
        if (newToken) {
          return fetchPlaylistTracks(playlistId, newOffset, newToken, true);
        }
      }
      setError('Failed to load playlist tracks');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaylistClick = (playlist) => {
    setSelectedPlaylist(playlist);
    fetchPlaylistTracks(playlist.id, 0);
  };

  const handleRefresh = () => {
    if (activeTab === 'liked') {
      // Generate random offset within total songs
      const randomOffset = Math.floor(Math.random() * 50) * 10; // Random page
      fetchLikedSongs(spotifyToken, randomOffset);
    } else if (selectedPlaylist) {
      const randomOffset = Math.floor(Math.random() * 50) * 10;
      fetchPlaylistTracks(selectedPlaylist.id, randomOffset);
    } else {
      // Refresh playlists list
      fetchPlaylists(spotifyToken);
    }
  };

  const handleAddToQueue = async () => {
    if (!selectedTrack) return;

    try {
      // Convert Spotify track to our track format matching QueueTrack model
      const trackData = {
        track_id: selectedTrack.id,
        title: selectedTrack.name,
        artist: selectedTrack.artist,
        preview_url: selectedTrack.preview_url,
        spotify_uri: selectedTrack.uri,
        duration_ms: selectedTrack.duration_ms,
        artwork: selectedTrack.album_art
      };

      await axios.post(
        `${API}/jam/${sessionId}/queue`,
        trackData,
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      
      onSelectTrack(trackData);
      onClose();
    } catch (err) {
      console.error('Failed to add track to queue:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to add track to queue';
      alert(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  if (!isOpen) return null;

  // Not connected to Spotify
  if (!spotifyToken || error === 'Please connect Spotify in Social Hub first') {
    return (
      <div className="jam-modal-overlay" onClick={onClose}>
        <div className="jam-modal-content spotify-library-modal" onClick={(e) => e.stopPropagation()}>
          <div className="jam-modal-header">
            <h2>🎵 Spotify Library</h2>
            <button className="jam-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="jam-modal-body" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
              Please connect your Spotify account in the Social Hub first.
            </p>
            <p style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)' }}>
              Go to Social Hub → Connect Spotify → Return here
            </p>
          </div>
          <div className="jam-modal-footer">
            <button className="jam-btn jam-btn-secondary" onClick={onClose} style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter tracks based on search and genre
  const filterTracks = (tracks) => {
    return tracks.filter(track => {
      const matchesSearch = searchQuery === '' || 
        track.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        track.artist.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGenre = selectedGenre === '' || 
        track.name.toLowerCase().includes(selectedGenre.toLowerCase()) ||
        track.artist.toLowerCase().includes(selectedGenre.toLowerCase()) ||
        track.album.toLowerCase().includes(selectedGenre.toLowerCase());
      
      return matchesSearch && matchesGenre;
    });
  };

  const currentTracks = filterTracks(activeTab === 'liked' ? likedSongs : playlistTracks);

  return (
    <div className="jam-modal-overlay" onClick={onClose}>
      <div className="jam-modal-content spotify-library-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', overflow: 'visible' }}>
        <div className="jam-modal-header">
          <h2>🎵 Spotify Library</h2>
          <button className="jam-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Search and Filter */}
        <div style={{ padding: '0 1.5rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <input
              type="text"
              placeholder="Search tracks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.9rem'
              }}
            />
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'rgba(20, 20, 35, 0.95)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.9rem',
                minWidth: '120px'
              }}
            >
              <option value="" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>All Genres</option>
              <option value="house" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>House</option>
              <option value="techno" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Techno</option>
              <option value="trance" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Trance</option>
              <option value="edm" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>EDM</option>
              <option value="dubstep" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Dubstep</option>
              <option value="drum and bass" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Drum & Bass</option>
              <option value="ambient" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Ambient</option>
              <option value="downtempo" style={{ background: 'rgba(20, 20, 35, 0.95)', color: 'white' }}>Downtempo</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="spotify-library-tabs" style={{ display: 'flex', borderBottom: '2px solid rgba(0, 255, 255, 0.2)', marginBottom: '1rem' }}>
          <button
            onClick={() => {
              setActiveTab('liked');
              setSelectedPlaylist(null);
              setSelectedTrack(null);
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: activeTab === 'liked' ? 'rgba(0, 255, 255, 0.2)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'liked' ? '2px solid #00ffff' : 'none',
              color: activeTab === 'liked' ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeTab === 'liked' ? '600' : '400'
            }}
          >
            ❤️ Liked Songs
          </button>
          <button
            onClick={() => {
              setActiveTab('playlists');
              setSelectedTrack(null);
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: activeTab === 'playlists' ? 'rgba(0, 255, 255, 0.2)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'playlists' ? '2px solid #00ffff' : 'none',
              color: activeTab === 'playlists' ? '#00ffff' : 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              fontSize: '0.95rem',
              fontWeight: activeTab === 'playlists' ? '600' : '400'
            }}
          >
            📂 Playlists
          </button>
        </div>

        <div className="jam-modal-body" style={{ maxHeight: '500px', overflow: 'auto' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading...</p>
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4444' }}>
              <p>{error}</p>
            </div>
          )}

          {/* Liked Songs Tab */}
          {activeTab === 'liked' && !loading && (
            <div className="spotify-tracks-list">
              {currentTracks.map((track) => (
                <div
                  key={track.id}
                  onClick={() => setSelectedTrack(track)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    background: selectedTrack?.id === track.id ? 'rgba(0, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                    border: selectedTrack?.id === track.id ? '2px solid #00ffff' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    if (selectedTrack?.id !== track.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedTrack?.id !== track.id) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }
                  }}
                >
                  {track.album_art && (
                    <img src={track.album_art} alt={track.name} style={{ width: '50px', height: '50px', borderRadius: '4px' }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {track.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {track.artist}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Playlists Tab */}
          {activeTab === 'playlists' && !loading && !selectedPlaylist && (
            <div className="spotify-playlists-list">
              {playlists.map((playlist) => (
                <div
                  key={playlist.id}
                  onClick={() => handlePlaylistClick(playlist)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
                >
                  {playlist.images && playlist.images.length > 0 && (
                    <img src={playlist.images[0].url} alt={playlist.name} style={{ width: '50px', height: '50px', borderRadius: '4px' }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {playlist.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {playlist.tracks_total} tracks
                    </div>
                  </div>
                  <span style={{ fontSize: '1.2rem' }}>→</span>
                </div>
              ))}
            </div>
          )}

          {/* Playlist Tracks */}
          {activeTab === 'playlists' && !loading && selectedPlaylist && (
            <div>
              <button
                onClick={() => setSelectedPlaylist(null)}
                style={{
                  marginBottom: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '6px',
                  color: '#00ffff',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                ← Back to Playlists
              </button>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>{selectedPlaylist.name}</h3>
              <div className="spotify-tracks-list">
                {currentTracks.map((track) => (
                  <div
                    key={track.id}
                    onClick={() => setSelectedTrack(track)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: selectedTrack?.id === track.id ? 'rgba(0, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: selectedTrack?.id === track.id ? '2px solid #00ffff' : '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      if (selectedTrack?.id !== track.id) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedTrack?.id !== track.id) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                  >
                    {track.album_art && (
                      <img src={track.album_art} alt={track.name} style={{ width: '50px', height: '50px', borderRadius: '4px' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {track.name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {track.artist}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="jam-modal-footer" style={{ justifyContent: 'space-between' }}>
          {onBack && (
            <button 
              className="jam-btn jam-btn-secondary" 
              onClick={onBack}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              ← Back
            </button>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            <button 
              className="jam-btn jam-btn-secondary" 
              onClick={handleRefresh}
              disabled={loading}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              🔄 Refresh
            </button>
            <button 
              className="jam-btn jam-btn-primary"
              onClick={handleAddToQueue}
              disabled={!selectedTrack}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              Add to Queue
            </button>
            <button 
              className="jam-btn jam-btn-secondary" 
              onClick={onClose}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotifyLibraryModal;
